import { useEffect } from 'react'
import type { MapboxCustomLayerService } from '@/services/3d'
import type { ZoneGeometry } from '../engine/types'

/**
 * Bridge live zone-geometry edits onto the rendered area footprint.
 *
 * The ramp/tiedown footprints are streamed into the scene once from the DB and
 * are otherwise immutable — editing `ZoneGeometry` (move / rotate / resize in the
 * Zone Placement panel) only re-flows the aircraft slot grid, not the pad itself.
 * Without this, rotating or moving an area would slide the parked aircraft off a
 * stationary pad. This hook pushes each zone's transform onto its building group
 * (matched by `areaId === userData.buildingId`) so the footprint tracks the edits.
 *
 * Move/rotate are applied as absolute transforms; resize as an X/Z group scale
 * relative to the as-loaded dimensions (`zoneBaseGeom`). At load `zones` equals
 * the base geometry, so the first pass is a no-op (the mesh already renders at
 * those values) — it only does real work after the user edits a zone, by which
 * point the footprints have long since finished streaming in. The synthetic
 * fallback apron has no mesh, so its update is a harmless no-op.
 */
export function useEventZoneSync(
  layerService: MapboxCustomLayerService | null,
  zones: ZoneGeometry[],
  zoneBaseGeom: Record<string, { width: number; depth: number }>
) {
  useEffect(() => {
    if (!layerService) return
    for (const z of zones) {
      const base = zoneBaseGeom[z.areaId]
      layerService.updateBuildingTransform(z.areaId, {
        x: z.center[0],
        z: z.center[1],
        rotationDeg: z.rotationDeg,
        scaleX: base && base.width > 0 ? z.width / base.width : undefined,
        scaleZ: base && base.depth > 0 ? z.depth / base.depth : undefined,
      })
    }
  }, [layerService, zones, zoneBaseGeom])

  return null
}
